import { useState, useEffect, useRef } from "react";
import type React from "react";
import { useLocation } from "wouter";
import { ArrowLeft, Globe, Trophy, TrendingUp, TrendingDown, Flame, Users, BarChart2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetNationalRanking,
  useGetNationalRankingStats,
  getGetNationalRankingQueryKey,
} from "@workspace/api-client-react";
import type {
  NationalRankingEntry,
  NationalRankingStats,
} from "@workspace/api-client-react";
import { useAuth } from "@/hooks/useAuth";
import { PlayerPreviewModal } from "@/components/PlayerPreviewModal";

import { getCountryName } from "@workspace/profile-completion";

const PAGE_SIZE = 50;

function flagUrl(code: string) {
  return `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
}

function entryName(entry: NationalRankingEntry): string {
  return (
    [entry.firstName, entry.lastName].filter(Boolean).join(" ").trim() ||
    "Jugador"
  );
}

function entryWinRate(entry: NationalRankingEntry): number {
  if (!entry.matchesPlayed) return 0;
  return Math.round((entry.matchesWon / entry.matchesPlayed) * 100);
}

type Tab = "male" | "female" | "stats";

const TABS: { key: Tab; label: string }[] = [
  { key: "male", label: "Hombres" },
  { key: "female", label: "Mujeres" },
  { key: "stats", label: "Estadísticas" },
];

function StatRow({
  label,
  value,
  sub,
  icon,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="pt-3 pb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
          {icon}
          <span className="truncate">{label}</span>
        </div>
        <div className="text-right shrink-0">
          <p className="font-bold text-sm">{value}</p>
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function StatsPanel({ stats }: { stats: NationalRankingStats }) {
  const total = stats.totalActive + stats.totalInactive;
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <Card>
          <CardContent className="pt-3 pb-3 flex flex-col items-center gap-0.5">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" /> Total
            </span>
            <span className="font-bold text-lg tabular-nums">{total}</span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 pb-3 flex flex-col items-center gap-0.5">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> Activos
            </span>
            <span className="font-bold text-lg tabular-nums">{stats.totalActive}</span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 pb-3 flex flex-col items-center gap-0.5">
            <span className="text-xs text-muted-foreground">Sin partidos</span>
            <span className="font-bold text-lg tabular-nums">{stats.totalInactive}</span>
          </CardContent>
        </Card>
      </div>

      {(stats.trendingUp ?? []).length > 0 && (
        <StatRow
          icon={<TrendingUp className="h-3.5 w-3.5 text-emerald-500" />}
          label="En racha ascendente"
          value={(stats.trendingUp ?? []).map((p) => p.name).join(", ")}
          sub={(stats.trendingUp ?? [])[0]?.delta != null && (stats.trendingUp ?? [])[0]!.delta! > 0
            ? `+${(stats.trendingUp ?? [])[0]!.delta!.toFixed(2)} este mes`
            : undefined}
        />
      )}

      {(stats.trendingDown ?? []).length > 0 && (
        <StatRow
          icon={<TrendingDown className="h-3.5 w-3.5 text-red-500" />}
          label="Bajando este mes"
          value={(stats.trendingDown ?? []).map((p) => p.name).join(", ")}
          sub={(stats.trendingDown ?? [])[0]?.delta != null && (stats.trendingDown ?? [])[0]!.delta! < 0
            ? `${(stats.trendingDown ?? [])[0]!.delta!.toFixed(2)} este mes`
            : undefined}
        />
      )}

      {(stats.mostActiveMonth ?? []).length > 0 && (
        <StatRow
          icon={<Flame className="h-3.5 w-3.5 text-orange-500" />}
          label="Más activo del mes"
          value={(stats.mostActiveMonth ?? []).map((p) => p.name).join(", ")}
          sub={(stats.mostActiveMonth ?? [])[0]?.matchesMonthCount != null
            ? `${(stats.mostActiveMonth ?? [])[0]!.matchesMonthCount!} partidos este mes`
            : undefined}
        />
      )}

      {stats.leader && (
        <StatRow
          icon={<Trophy className="h-3.5 w-3.5 text-amber-500" />}
          label="Líder"
          value={stats.leader.name}
          sub={stats.leader.nationalRank != null ? `#${stats.leader.nationalRank}` : undefined}
        />
      )}

      {stats.longestStreak && (
        <StatRow
          icon={<TrendingUp className="h-3.5 w-3.5 text-emerald-500" />}
          label="Racha más larga"
          value={stats.longestStreak.name}
          sub={
            stats.longestStreak.streak != null && stats.longestStreak.streak > 0
              ? `${stats.longestStreak.streak} victorias seguidas`
              : undefined
          }
        />
      )}

      {stats.mostMatches && (
        <StatRow
          icon={<BarChart2 className="h-3.5 w-3.5" />}
          label="Más partidos jugados"
          value={stats.mostMatches.name}
          sub={
            stats.mostMatches.matchesPlayedCount != null
              ? `${stats.mostMatches.matchesPlayedCount} partidos`
              : undefined
          }
        />
      )}
    </div>
  );
}

function RankingList({
  ranking,
  rankLoading,
  userId,
  onPlayerClick,
  hasMore,
  onLoadMore,
  loadingMore,
}: {
  ranking: NationalRankingEntry[];
  rankLoading: boolean;
  userId?: string;
  onPlayerClick: (playerId: string) => void;
  hasMore: boolean;
  onLoadMore: () => void;
  loadingMore: boolean;
}) {
  if (rankLoading) {
    return (
      <div className="space-y-1 px-4 pb-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded-md" />
        ))}
      </div>
    );
  }
  if (ranking.length === 0) {
    return (
      <div className="px-4 pb-6 pt-2 text-center text-muted-foreground text-sm">
        Aún no hay jugadores activos en esta categoría.
      </div>
    );
  }
  return (
    <div>
      <div className="divide-y divide-border">
        {ranking.map((entry) => {
          const isMe = entry.userId === userId;
          const name = entryName(entry);
          const winRate = entryWinRate(entry);
          const inactive = !entry.isActive;
          return (
            <button
              key={entry.userId}
              type="button"
              onClick={() => onPlayerClick(entry.userId)}
              className={`w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors ${isMe ? "bg-primary/5" : ""} ${inactive ? "opacity-50" : ""}`}
            >
              <span
                className={`w-7 text-center font-bold text-sm tabular-nums ${
                  entry.nationalRank === 1
                    ? "text-amber-500"
                    : entry.nationalRank === 2
                      ? "text-slate-400"
                      : entry.nationalRank === 3
                        ? "text-amber-700"
                        : "text-muted-foreground"
                }`}
              >
                {inactive ? "-" : entry.nationalRank != null ? `#${entry.nationalRank}` : "-"}
              </span>
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarImage src={entry.profileImageUrl ?? undefined} />
                <AvatarFallback>{name[0]?.toUpperCase() ?? "?"}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate leading-tight">
                  {name}
                  {isMe && (
                    <Badge variant="secondary" className="ml-2 text-xs py-0">
                      Tú
                    </Badge>
                  )}
                  {inactive && (
                    <Badge variant="outline" className="ml-2 text-xs py-0 text-muted-foreground">
                      Sin partidos
                    </Badge>
                  )}
                  {inactive && (
                    <span className="block text-xs text-primary/70 mt-0.5">
                      Registra tu primer partido
                    </span>
                  )}
                </p>
                {!inactive && (
                  <p className="text-xs text-muted-foreground">
                    {entry.matchesPlayed}{" "}
                    {entry.matchesPlayed === 1 ? "partido" : "partidos"}
                    {" · "}
                    {winRate}% victorias
                  </p>
                )}
              </div>
              {!inactive && (
                <span className="text-sm font-semibold tabular-nums text-primary">
                  {(entry.skillLevel ?? 0).toFixed(2)}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {hasMore && (
        <div className="px-4 py-3 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Cargando..." : "Mostrar más"}
          </Button>
        </div>
      )}
    </div>
  );
}

export function NationalLeaguePage() {
  const [, navigate] = useLocation();
  const { user } = useAuth() as { user: { id?: string } | null };
  const [tab, setTab] = useState<Tab>("male");
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [allEntries, setAllEntries] = useState<NationalRankingEntry[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const prevTabRef = useRef<Tab>(tab);

  const genderParam = tab !== "stats" ? { gender: (tab === "male" ? "M" : "F") as "M" | "F" } : {};
  const rankingParams = tab !== "stats" ? { ...genderParam, page, limit: PAGE_SIZE } : undefined;
  const { data: rankingData, isLoading: rankLoading, isFetching } =
    useGetNationalRanking(
      rankingParams,
      { query: { queryKey: getGetNationalRankingQueryKey(rankingParams), enabled: tab !== "stats" } }
    );
  const { data: stats, isLoading: statsLoading } = useGetNationalRankingStats();

  useEffect(() => {
    if (prevTabRef.current !== tab) {
      prevTabRef.current = tab;
      setPage(1);
      setAllEntries([]);
      setTotal(null);
      setHasMore(false);
    }
  }, [tab]);

  useEffect(() => {
    if (!rankingData) return;
    if (page === 1) {
      setAllEntries(rankingData.ranking);
    } else {
      setAllEntries((prev) => [...prev, ...rankingData.ranking]);
    }
    setTotal(rankingData.total);
    setHasMore(rankingData.hasMore);
  }, [rankingData, page]);

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    setPage((p) => p + 1);
  };

  const countryCode = stats?.country ?? "";
  const isFirstLoad = rankLoading && page === 1;
  const loadingMore = isFetching && page > 1;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
      <button
        onClick={() => navigate("/ligas")}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Volver a ligas"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a Ligas
      </button>

      <div className="flex items-center gap-4">
        {statsLoading ? (
          <div className="h-10 w-14 rounded bg-muted animate-pulse" />
        ) : countryCode ? (
          <img
            src={flagUrl(countryCode)}
            alt={getCountryName(countryCode)}
            className="h-10 w-auto rounded shadow-sm"
          />
        ) : null}
        <div>
          <h1 className="text-2xl font-bold leading-tight">
            Liga Nacional
            {countryCode ? ` · ${getCountryName(countryCode)}` : ""}
          </h1>
          <p className="text-sm text-muted-foreground">
            Ranking automático por país y género
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <Button
            key={t.key}
            variant={tab === t.key ? "default" : "outline"}
            size="sm"
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {tab === "stats" ? (
        statsLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="pt-4 pb-4 space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-6 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : stats ? (
          <StatsPanel stats={stats} />
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            No hay estadísticas disponibles.
          </p>
        )
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <span>
                Clasificación · {tab === "male" ? "Hombres" : "Mujeres"}
              </span>
              {total != null && !isFirstLoad && (
                <span className="ml-auto text-xs font-normal text-muted-foreground tabular-nums">
                  mostrando {allEntries.length} de {total}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <RankingList
              ranking={allEntries}
              rankLoading={isFirstLoad}
              userId={user?.id}
              onPlayerClick={(playerId) => setSelectedPlayerId(playerId)}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              loadingMore={loadingMore}
            />
          </CardContent>
        </Card>
      )}
      <PlayerPreviewModal
        playerId={selectedPlayerId}
        open={!!selectedPlayerId}
        onOpenChange={(open) => { if (!open) setSelectedPlayerId(null); }}
      />
    </div>
  );
}
